/* eslint-disable max-len */
/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useLayoutEffect, useState } from 'react';

import { HistoryOfferItem, HistoryOfferItemShortReasonEnum } from 'client-code/recordings';

import { AppUtils } from '../../../../../../core/AppUtils';
import { ServicesClientImpl } from '../../../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../../../core/locale';
import { DateTime } from '../../../../../../core/utils';
import { Mousetrap } from '../../../../../common';
import { ProgramCell } from '../../../../../Guide/ProgramCell';
import { CellTypes } from '../../../../../Guide/ProgramCell/CellTypes';
import {
    getRecordingOfferItemIconClassname,
    getRecordingOfferItemReason,
    getRecordingOfferItemTitle,
} from '../../RecordingActivity.utils';
import { RecordingActivityHighlight } from '../../RecordingActivityHighlight/RecordingActivityHighlight';

import '../RecordingActivityContent.scss';

export type RecordingActivityItemProps = {
    item: HistoryOfferItem;
    rowIndex: number;
};

const RecordingActivityOfferItem = ({ item, rowIndex }: RecordingActivityItemProps) => {
    // eslint-disable-next-line no-unused-vars
    const [_stateUpdater, setStateUpdater] = useState(0);
    const services = new ServicesClientImpl();
    const shouldHideMetadata = services.shouldHideMetadata();

    useLayoutEffect(() => {
        // The ProgramCell overflow mechanism relies on the component re-rendering so that its refs are correctly populated.
        // This component is very small and has no state, so it doesn't re-render until a keypress occurs.
        // I tried several ways to fix this issue inside ProgramCell, but each approach caused unexpected bugs in the main guide,
        // such as: overflow no longer working, the small left/right icons at the end of the table overlapping with text,
        // and recording icons overlapping with other content.
        // As a result, the only reliable solution was to force a re-render from the parent component.
        // This is not ideal, but it's currently the only workaround that ensures no regression.
        setStateUpdater((prev) => prev + 1);
    }, []);

    return (
        <div
            className="recording-activity-list-offer-item"
            data-testid="recording-activity-list-item"
        >
            <Mousetrap value={AppUtils.createHighlightId(0, rowIndex)}>
                <RecordingActivityHighlight id={AppUtils.createHighlightId(0, rowIndex)} />
                <div className="recording-activity-list-row">
                    <div
                        className="recording-activity-row-cell status-icon"
                        data-testid="recording-activity-item-status-icon"
                    >
                        <div
                            className={getRecordingOfferItemIconClassname(item.deletedReason)}
                        ></div>
                    </div>
                    <div
                        className="recording-activity-row-cell start-time"
                        data-testid="recording-activity-item-start-time"
                    >
                        {new DateTime(item.recordingTime).to12HourClock()}
                    </div>
                    <div
                        className="recording-activity-row-cell program-cell"
                        data-testid="recording-activity-item-program-cell"
                    >
                        <ProgramCell
                            title={
                                shouldHideMetadata && item.isAdult
                                    ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
                                    : getRecordingOfferItemTitle(item)
                            }
                            isSubscribed={true}
                            type={CellTypes.DEFAULT}
                            duration={100}
                            movieYear={shouldHideMetadata && item.isAdult ? null : item.movieYear}
                            isNew={item.isNew}
                        />
                    </div>
                    <div
                        className="recording-activity-row-cell reason-cell"
                        data-testid="recording-activity-item-reason"
                    >
                        {getRecordingOfferItemReason(
                            item.shortReason ?? HistoryOfferItemShortReasonEnum.Canceled,
                        )}
                    </div>
                </div>
            </Mousetrap>
        </div>
    );
};

export default RecordingActivityOfferItem;
